// Page feedback — shared client helpers for odv-truest.
// Doug LOCK 2026-09-29: every page = one Brain node; feedback routes ONE task to
// the page's owner seat (see docs/architecture/PAGE_FEEDBACK_ROUTING.md).
// Doug LOCK 2026-10-03: no pill or prompt. Explicit feedback is only the
// profile-menu "Send feedback" dialog (components/SendFeedbackDialog.jsx); the
// subview below also keys the passive UX signals (components/PassiveUxSignals.jsx).
//
// Pages whose ownership changes inside one route (CRM tabs, portal shuttle mode)
// report a subview so the registry can tell them apart (`/crm#pipeline`).
import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '@/lib/supabase';

let current = { path: null, subview: null };
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

export function setPageFeedbackSubview(subview) {
  const path = typeof window === 'undefined' ? null : window.location.pathname;
  const next = { path, subview: subview || null };
  if (next.path === current.path && next.subview === current.subview) return;
  current = next;
  emit();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return current;
}

/** Subview reported by the current page, only while that page is still mounted. */
export function useCurrentPageFeedbackSubview(pathname) {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return snap.path === pathname ? snap.subview : null;
}

/** Call from a page whose owner differs by tab/mode, e.g. usePageFeedbackSubview(activeTab). */
export function usePageFeedbackSubview(subview) {
  useEffect(() => {
    setPageFeedbackSubview(subview);
    return () => setPageFeedbackSubview(null);
  }, [subview]);
}

/** POST one verdict through the existing app-feedback edge function. */
export async function sendPageVerdict({ verdict, screen, subview, note }) {
  const { data, error } = await supabase.functions.invoke('app-feedback', {
    body: {
      type: 'feedback',
      app: 'odv-truest',
      feature: 'page_verdict',
      feedback_type: verdict === 'works' ? 'thumbs_up' : 'thumbs_down',
      screen,
      message: note || null,
      metadata: {
        subview: subview || null,
        verdict,
        url: typeof window === 'undefined' ? null : window.location.pathname + window.location.search,
        title: typeof document === 'undefined' ? null : document.title,
        host: typeof window === 'undefined' ? null : window.location.host,
      },
    },
  });
  if (error) throw error;
  return data || {};
}
